import { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import {
  MapPin, Phone, Mail, Clock, Send, Loader2, User, GraduationCap,
  MessageSquare, ArrowRight, ExternalLink, CalendarHeart, Users2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

const GRADE_LABELS_FR = {
  "6e": "6ème", "5e": "5ème", "4e": "4ème", "3e": "3ème",
  "2nde": "2nde", "1ere": "1ère", "terminale": "Terminale",
};

const GENDER_LABELS_FR = { M: "Masculin", F: "Féminin" };

const SCHOOL_EMAIL = "collegelyceelafayette@gmail.com";

function buildMailtoLink(form, lang) {
  const gradeLabel = GRADE_LABELS_FR[form.grade] || form.grade;
  const subject = `[MyLafayette] Demande de rendez-vous — ${form.parent_name} (${gradeLabel})`;
  const body = [
    "Bonjour,",
    "",
    "Je souhaite prendre rendez-vous pour une admission à Lafayette International School.",
    "",
    "— INFORMATIONS —",
    `Parent / tuteur          : ${form.parent_name}`,
    `Email                    : ${form.email}`,
    `Téléphone                : ${form.phone || "(non renseigné)"}`,
    `Date de naissance enfant : ${form.child_dob || "(non renseignée)"}`,
    `Genre de l'enfant        : ${GENDER_LABELS_FR[form.child_gender] || "(non renseigné)"}`,
    `Niveau souhaité          : ${gradeLabel}`,
    `Langue                   : ${(lang || "fr").toUpperCase()}`,
    "",
    "— MESSAGE —",
    form.message?.trim() || "(aucun message ajouté)",
    "",
    "Cordialement,",
    form.parent_name,
  ].join("\n");
  return `mailto:${SCHOOL_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

async function postWeb3Forms(form, lang) {
  if (!WEB3FORMS_KEY) return false;
  const gradeLabel = GRADE_LABELS_FR[form.grade] || form.grade;
  // Use FormData — Web3Forms accepts multipart and it avoids the JSON CORS preflight.
  const fd = new FormData();
  fd.append("access_key", WEB3FORMS_KEY);
  fd.append("subject", `[MyLafayette] Nouvelle demande de RDV — ${form.parent_name} (${gradeLabel})`);
  fd.append("from_name", "MyLafayette Admissions");
  fd.append("replyto", form.email);
  fd.append("name", form.parent_name);
  fd.append("email", form.email);
  fd.append("phone", form.phone || "(non renseigné)");
  fd.append("child_date_of_birth", form.child_dob || "(non renseignée)");
  fd.append("child_gender", GENDER_LABELS_FR[form.child_gender] || "(non renseigné)");
  fd.append("grade", gradeLabel);
  fd.append("language", (lang || "fr").toUpperCase());
  fd.append("message", form.message?.trim() || "(aucun message)");
  fd.append("botcheck", "");
  try {
    const res = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: fd,
    });
    const json = await res.json().catch(() => ({}));
    return res.ok && json.success === true;
  } catch (_err) {
    return false;
  }
}

const GRADE_LABEL_FIELDS = { en: "labelEn", zh: "labelZh", ru: "labelRu", fr: "labelFr" };
function gradeLabel(grade, lang) {
  return grade[GRADE_LABEL_FIELDS[lang] || GRADE_LABEL_FIELDS.fr];
}

const GRADES = [
  { value: "6e",        labelFr: "6ème (11-12 ans)",       labelEn: "6ème (11-12 years)",      labelZh: "6ème(初一 · 11-12岁)",  labelRu: "6ème (11–12 лет)" },
  { value: "5e",        labelFr: "5ème (12-13 ans)",       labelEn: "5ème (12-13 years)",      labelZh: "5ème(初二 · 12-13岁)",  labelRu: "5ème (12–13 лет)" },
  { value: "4e",        labelFr: "4ème (13-14 ans)",       labelEn: "4ème (13-14 years)",      labelZh: "4ème(初三 · 13-14岁)",  labelRu: "4ème (13–14 лет)" },
  { value: "3e",        labelFr: "3ème (14-15 ans)",       labelEn: "3ème (14-15 years)",      labelZh: "3ème(高一 · 14-15岁)",  labelRu: "3ème (14–15 лет)" },
  { value: "2nde",      labelFr: "2nde (15-16 ans)",       labelEn: "2nde (15-16 years)",      labelZh: "2nde(高二 · 15-16岁)",  labelRu: "2nde (15–16 лет)" },
  { value: "1ere",      labelFr: "1ère (16-17 ans)",       labelEn: "1ère (16-17 years)",      labelZh: "1ère(高三 · 16-17岁)",  labelRu: "1ère (16–17 лет)" },
  { value: "terminale", labelFr: "Terminale (17-18 ans)",  labelEn: "Terminale (17-18 years)", labelZh: "Terminale(毕业班 · 17-18岁)", labelRu: "Terminale (17–18 лет)" },
];

export default function Contact() {
  const { t, lang } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    phone: "",
    child_dob: "",
    child_gender: "",
    grade: "",
    message: "",
  });

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setGender = (g) => setForm((f) => ({ ...f, child_gender: f.child_gender === g ? "" : g }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name.trim() || !form.email.trim() || !form.grade) {
      toast.error(t("contact.errorRequired"));
      return;
    }
    setSubmitting(true);
    try {
      // 1) Save to our backend (admin tracking, always)
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Submit failed");
      }
      // 2) Send the actual email via Web3Forms (client-side).
      //    Non-blocking for UX — failure does not break the form submission.
      postWeb3Forms(form, lang).catch(() => {});
      // 3) Keep a copy of the submitted form so we can offer a mailto fallback.
      setLastSubmission({ ...form });
      setDone(true);
      toast.success(t("contact.successToast"));
    } catch (err) {
      toast.error(err.message || t("contact.errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header strip */}
      <section className="relative overflow-hidden bg-slate-50 grain border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 pt-12 sm:pt-16 pb-10 sm:pb-14 text-center">
          <span className="overline" data-testid="contact-overline">{t("contact.overline")}</span>
          <h1 className="font-display font-bold text-3xl sm:text-5xl lg:text-6xl mt-3 leading-[0.98]" data-testid="contact-title">
            {t("contact.titlePart1")} <span className="font-serif-italic" style={{ color: "var(--c-accent)" }}>{t("contact.titlePart2")}</span>
          </h1>
          <p className="text-slate-600 mt-5 max-w-2xl mx-auto text-sm sm:text-base">
            {t("contact.subtitle")}
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* LEFT: Contact info */}
          <aside className="lg:col-span-5 space-y-6">
            <div>
              <span className="overline">{t("contact.infoOverline")}</span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2" style={{ color: "var(--c-primary)" }}>
                {t("contact.infoTitle")}
              </h2>
            </div>

            <ul className="space-y-4">
              <li className="card p-5 flex items-start gap-4">
                <span className="w-10 h-10 rounded-md grid place-items-center shrink-0" style={{ background: "var(--c-primary)", color: "var(--c-accent)" }}>
                  <MapPin size={18}/>
                </span>
                <div className="min-w-0">
                  <div className="overline">{t("contact.addressLabel")}</div>
                  <div className="font-medium mt-1">10 Avenue Georges Clemenceau</div>
                  <div className="text-sm text-slate-600">06000 Nice, France</div>
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=10+Avenue+Georges+Clemenceau+06000+Nice"
                    target="_blank" rel="noopener noreferrer"
                    data-testid="contact-maps-link"
                    className="text-xs mt-2 inline-flex items-center gap-1 hover:underline"
                    style={{ color: "var(--c-cta)" }}
                  >
                    {t("contact.directions")} <ExternalLink size={11}/>
                  </a>
                </div>
              </li>

              <li className="card p-5 flex items-start gap-4">
                <span className="w-10 h-10 rounded-md grid place-items-center shrink-0" style={{ background: "var(--c-primary)", color: "var(--c-accent)" }}>
                  <Phone size={18}/>
                </span>
                <div className="min-w-0">
                  <div className="overline">{t("contact.phoneLabel")}</div>
                  <a href="tel:+33493620029" className="font-medium mt-1 block hover:underline" data-testid="contact-phone-link">
                    +33 4 93 62 00 29
                  </a>
                  <div className="text-xs text-slate-500 mt-1">{t("contact.phoneHours")}</div>
                </div>
              </li>

              <li className="card p-5 flex items-start gap-4">
                <span className="w-10 h-10 rounded-md grid place-items-center shrink-0" style={{ background: "var(--c-primary)", color: "var(--c-accent)" }}>
                  <Mail size={18}/>
                </span>
                <div className="min-w-0">
                  <div className="overline">{t("contact.emailLabel")}</div>
                  <a
                    href="mailto:collegelyceelafayette@gmail.com"
                    className="font-medium mt-1 block hover:underline break-all"
                    data-testid="contact-email-link"
                  >
                    collegelyceelafayette@gmail.com
                  </a>
                  <div className="text-xs text-slate-500 mt-1">{t("contact.emailReply")}</div>
                </div>
              </li>

              <li className="card p-5 flex items-start gap-4">
                <span className="w-10 h-10 rounded-md grid place-items-center shrink-0" style={{ background: "var(--c-primary)", color: "var(--c-accent)" }}>
                  <Clock size={18}/>
                </span>
                <div className="min-w-0">
                  <div className="overline">{t("contact.hoursLabel")}</div>
                  <div className="text-sm mt-1">{t("contact.hoursMon")}</div>
                  <div className="text-sm">{t("contact.hoursWed")}</div>
                </div>
              </li>
            </ul>
          </aside>

          {/* RIGHT: Form */}
          <div className="lg:col-span-7">
            <div className="card p-6 sm:p-10" style={{ background: "var(--c-bg)" }}>
              <span className="overline">{t("contact.formOverline")}</span>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2" style={{ color: "var(--c-primary)" }}>
                {t("contact.formTitle")}
              </h2>
              <p className="text-sm text-slate-600 mt-2">{t("contact.formSub")}</p>

              {done ? (
                <div data-testid="contact-success" className="mt-8 p-6 rounded-md border" style={{ borderColor: "var(--c-status-validated)", background: "#F0FDF4" }}>
                  <div className="font-display text-xl font-bold" style={{ color: "var(--c-status-validated)" }}>
                    {t("contact.successTitle")}
                  </div>
                  <p className="text-sm text-slate-700 mt-2">
                    {t("contact.successDesc")}
                  </p>
                  <div className="mt-5 rounded-md border bg-white px-4 py-3" style={{ borderColor: "var(--c-border)" }}>
                    <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--c-text-soft)" }}>
                      {t("contact.backupEmailTitle")}
                    </div>
                    <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                      {t("contact.backupEmailDesc")}
                    </p>
                    <a
                      data-testid="contact-mailto-backup"
                      href={lastSubmission ? buildMailtoLink(lastSubmission, lang) : `mailto:${SCHOOL_EMAIL}`}
                      className="btn-cta mt-3 text-sm inline-flex"
                    >
                      <Mail size={14}/> {t("contact.backupEmailButton")}
                    </a>
                  </div>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => { setDone(false); setLastSubmission(null); setForm({ parent_name: "", email: "", phone: "", child_dob: "", child_gender: "", grade: "", message: "" }); }}
                    className="btn-ghost mt-5 text-sm"
                  >
                    {t("contact.sendAnother")} <ArrowRight size={14}/>
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-7 space-y-5" data-testid="contact-form">
                  <div>
                    <label className="field-label" htmlFor="contact-parent">
                      <User size={11} className="inline mr-1 -mt-0.5"/> {t("contact.parentLabel")} *
                    </label>
                    <input
                      id="contact-parent"
                      data-testid="contact-input-name"
                      type="text"
                      required
                      className="field-input"
                      value={form.parent_name}
                      onChange={update("parent_name")}
                      placeholder={t("contact.parentPlaceholder")}
                      autoComplete="name"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="field-label" htmlFor="contact-email">
                        <Mail size={11} className="inline mr-1 -mt-0.5"/> {t("contact.emailFieldLabel")} *
                      </label>
                      <input
                        id="contact-email"
                        data-testid="contact-input-email"
                        type="email"
                        required
                        className="field-input"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="parent@email.com"
                        autoComplete="email"
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="contact-phone">
                        <Phone size={11} className="inline mr-1 -mt-0.5"/> {t("contact.phoneFieldLabel")}
                      </label>
                      <input
                        id="contact-phone"
                        data-testid="contact-input-phone"
                        type="tel"
                        className="field-input"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+33 6 12 34 56 78"
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="field-label" htmlFor="contact-child-dob">
                        <CalendarHeart size={11} className="inline mr-1 -mt-0.5"/> {t("contact.childDobLabel")}
                      </label>
                      <input
                        id="contact-child-dob"
                        data-testid="contact-input-child-dob"
                        type="date"
                        className="field-input"
                        value={form.child_dob}
                        onChange={update("child_dob")}
                      />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="contact-grade">
                        <GraduationCap size={11} className="inline mr-1 -mt-0.5"/> {t("contact.gradeLabel")} *
                      </label>
                      <select
                        id="contact-grade"
                        data-testid="contact-input-grade"
                        required
                        className="field-input"
                        value={form.grade}
                        onChange={update("grade")}
                      >
                        <option value="">{t("contact.gradePlaceholder")}</option>
                        {GRADES.map((g) => (
                          <option key={g.value} value={g.value}>{gradeLabel(g, lang)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <fieldset>
                    <legend className="field-label">
                      <Users2 size={11} className="inline mr-1 -mt-0.5"/> {t("contact.childGenderLabel")}
                    </legend>
                    <div className="grid grid-cols-2 gap-3 mt-1" role="group" aria-label={t("contact.childGenderLabel")}>
                      {[
                        { value: "M", label: t("contact.childGenderMale"), testid: "contact-gender-male" },
                        { value: "F", label: t("contact.childGenderFemale"), testid: "contact-gender-female" },
                      ].map((opt) => {
                        const active = form.child_gender === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            role="checkbox"
                            aria-checked={active}
                            data-testid={opt.testid}
                            onClick={() => setGender(opt.value)}
                            className={`flex items-center gap-3 rounded-md border px-4 py-3 text-sm transition-all ${
                              active
                                ? "border-slate-900 bg-slate-900 text-white shadow-sm"
                                : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                            }`}
                          >
                            <span
                              aria-hidden="true"
                              className={`w-4 h-4 rounded-sm border flex items-center justify-center text-[10px] font-bold ${
                                active ? "border-white bg-white text-slate-900" : "border-slate-300 bg-white text-transparent"
                              }`}
                            >
                              ✓
                            </span>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>

                  <div>
                    <label className="field-label" htmlFor="contact-message">
                      <MessageSquare size={11} className="inline mr-1 -mt-0.5"/> {t("contact.messageLabel")}
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-input-message"
                      rows={5}
                      className="field-input"
                      value={form.message}
                      onChange={update("message")}
                      placeholder={t("contact.messagePlaceholder")}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="btn-cta w-full justify-center"
                  >
                    {submitting ? (<><Loader2 size={16} className="animate-spin"/> {t("contact.submitting")}</>) : (<><Send size={16}/> {t("contact.submit")}</>)}
                  </button>

                  <p className="text-xs text-slate-500 leading-relaxed">
                    {t("contact.privacy")}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
